import type { Dispatch, SetStateAction } from 'react';

import { usePostUserAvatar } from '@repo/api/user/usePostUserAvatar';
import { CheckCircle2Icon, InfoIcon, UserIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { ImageCropper, useImageCropper } from '@/components/ui/image-cropper';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { useUpdateCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

/**
 * Props for the UserAvatarEditor component.
 * @property {boolean} open - Whether the avatar editor dialog is open.
 * @property {Dispatch<SetStateAction<boolean>>} onOpenChange - Callback to change the open state.
 */
interface UserAvatarEditorProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

export default function UserAvatarEditor(userAvatarEditorProps: UserAvatarEditorProps) {
  const { open, onOpenChange } = userAvatarEditorProps;
  const updateCredentials = useUpdateCredentials();
  const [searchParams] = useSearchParams();
  const authToken: string | undefined = searchParams.get('t') ?? undefined;
  const createUserAvatarMutation = usePostUserAvatar();
  const cropperControls = useImageCropper();

  const handleCropAvatar = async () => {
    const blob: Blob | null = await cropperControls.getCroppedImage();
    if (!blob) return;
    await createUserAvatarMutation
      .mutateAsync({
        b64: blob,
        authToken: authToken,
      })
      .then(({ avatarURL }) => {
        updateCredentials({ avatarURL: getAvatar(avatarURL) });
        onOpenChange(false);
        cropperControls.setImage(null);
      });
  };

  useEffect(() => {
    if (open) {
      createUserAvatarMutation.reset();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogBody className={'grow'}>
          <DialogHeader className={'border-border-primary border-b'}>
            <FeaturedIcon size={'lg'}>
              <UserIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <DialogTitle>Zaktualizuj swój avatar</DialogTitle>
              <DialogDescription className={'text-muted-foreground text-xs'}>Dodaj lub zmień zdjęcie, aby łatwiej Cię rozpoznać.</DialogDescription>
            </div>
          </DialogHeader>

          <ImageCropper cropperControls={cropperControls} aspectRatio={1} maxHeight={256} maxWidth={256} />
          <div className="p-4">
            <Alert
              variant={'destructive'}
              data-visible={!!createUserAvatarMutation.isError}
              className="overflow-hidden opacity-100 transition-all duration-400 data-[visible=false]:max-h-0 data-[visible=false]:opacity-0 data-[visible=true]:h-fit data-[visible=true]:opacity-100"
            >
              <InfoIcon />
              <AlertDescription>{createUserAvatarMutation.errorMessage.detail}</AlertDescription>
            </Alert>
          </div>
        </DialogBody>
        <DialogFooter className={'border-border-primary border-t'}>
          <Button className={'w-full md:w-fit'} type={'button'} variant={'tertiary'} onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button disabled={createUserAvatarMutation.isPending} className={'w-full md:w-fit'} type={'button'} aria-label="submit" onClick={handleCropAvatar}>
            <LoadingSwap isLoading={createUserAvatarMutation.isPending} className="flex items-center gap-2">
              <CheckCircle2Icon />
              Zapisz
            </LoadingSwap>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
